import {ConnectedPosition, Overlay, OverlayPositionBuilder} from "@angular/cdk/overlay";
import {ComponentPortal, DomPortalOutlet} from "@angular/cdk/portal";
import {CommonModule} from "@angular/common";
import {ApplicationRef, ChangeDetectorRef, Component, Directive, ElementRef, EventEmitter, HostBinding, HostListener, Injector, Input, Output, TemplateRef, ViewChild, ChangeDetectionStrategy} from "@angular/core";

import {Lookup} from "framework/services/utils.service";
import {OverlayComponent, OverlayConfig} from "framework/ui/overlays/overlay.component";
import {Future} from "framework/utils/concurrency";
import {Debouncer} from "framework/utils/debouncers";

@Component({
               template: `
                   <o3-overlay [config]="config">
                       <div (mouseenter)="hovering = true"
                            (mouseleave)="handleMouseLeave()"
                            style="max-height: 100%; overflow-y: auto">
                           <ng-container [ngTemplateOutlet]="template"
                                         [ngTemplateOutletContext]="templateContext"></ng-container>
                       </div>
                   </o3-overlay>`,
               changeDetection: ChangeDetectionStrategy.Eager,
               imports : [
                   CommonModule,
                   OverlayComponent
               ]
           })
export class TooltipComponent
{
    @Input() template: TemplateRef<any>;
    @Input() templateContext: Lookup<any>;
    @Input() config: OverlayConfig;
    @Input() host: TooltipComponentDirective;

    private m_hovering: boolean;
    get hovering(): boolean
    {
        return this.m_hovering;
    }

    set hovering(hovering: boolean)
    {
        this.m_hovering = hovering;
        if (this.m_hovering) this.host.cancelScheduledCloseAttempt();
    }

    private m_opening: boolean;
    get opening(): boolean
    {
        return this.m_opening;
    }

    set opening(opening: boolean)
    {
        this.m_opening = opening;
        if (this.m_opening) this.host.cancelScheduledCloseAttempt();
    }

    @ViewChild(OverlayComponent, {static: true}) private m_overlay: OverlayComponent;

    get overlayElement(): HTMLElement|null
    {
        return this.m_overlay?.overlay?.overlayElement ?? null;
    }

    openTooltip(): boolean
    {
        if (!this.m_overlay.isOpen)
        {
            this.m_overlay.toggleOverlay();
            this.manageTooltipOpening();
            return true;
        }

        return false;
    }

    private async manageTooltipOpening()
    {
        this.opening = true;
        await Future.delayed(OverlayComponent.animationDuration);
        this.opening = false;

        if (this.host) this.host.opened.emit();

        this.host.attemptCloseTooltip();
    }

    closeTooltip()
    {
        this.m_overlay.closeOverlay();
    }

    handleMouseLeave()
    {
        this.hovering = false;
        this.host.scheduleCloseTooltipAttempt();
    }
}

@Directive({
               selector  : "[csTooltip]",
               standalone: true,
               exportAs  : "csTooltip"
           })
export class TooltipComponentDirective
{
    @Input("csTooltip") template: TemplateRef<any>;

    @Input("csTooltipContext") templateContext: Lookup<any>;

    private m_disabled: boolean = false;
    @Input("csTooltipDisabled") set disabled(disabled: boolean)
    {
        this.m_disabled = disabled;

        if (this.m_disabled) this.closeTooltip();
    }

    @Input("csTooltipScrollable") scrollable: boolean = false;

    @Input("csTooltipProgrammatic") programmatic: boolean = false;

    @Input("csTooltipDelay") delay: number = 0;

    @Input("csTooltipPosition") position: TooltipPosition = "bottom";

    private m_overlay: TooltipComponent;
    private m_outlet: DomPortalOutlet;

    private readonly m_closeScheduler = new Debouncer(120, async () => this.attemptCloseTooltip());

    private m_open: boolean = false;

    private m_mouseOver: boolean;
    get mouseOver(): boolean
    {
        return this.m_mouseOver;
    }

    set mouseOver(mouseOver: boolean)
    {
        this.m_mouseOver = mouseOver;
        if (this.m_mouseOver) this.cancelScheduledCloseAttempt();
    }

    @HostBinding("class.cs-tooltip--open") get open(): boolean
    {
        return this.m_open;
    }

    @Output() opened = new EventEmitter<void>();

    constructor(private m_element: ElementRef,
                private m_cdr: ChangeDetectorRef,
                private m_cdkOverlay: Overlay,
                private m_positionBuilder: OverlayPositionBuilder,
                private m_appRef: ApplicationRef,
                private m_inj: Injector)
    {}

    public ngOnInit()
    {
        let config = OverlayConfig.dropdown({
                                                coverAnchorWhenDisplayed : false,
                                                showBackdrop             : false,
                                                positionStrategy         : this.m_positionBuilder.flexibleConnectedTo(this.m_element)
                                                                               .withPush(true)
                                                                               .withGrowAfterOpen(true)
                                                                               .withPositions(this.getPositions()),
                                                maxHeight                : "50vh",
                                                borderRadius             : 8,
                                                trapFocus                : false,
                                                dialogAnimationTranslateY: "0",
                                                scrollStrategy           : this.m_cdkOverlay.scrollStrategies.close()
                                            });
        if (this.scrollable) config.overlayClass = "o3-overlay-scrollable";

        let portal    = new ComponentPortal(TooltipComponent);
        this.m_outlet = new DomPortalOutlet(this.m_element.nativeElement, this.m_appRef, this.m_inj);
        let ref       = portal.attach(this.m_outlet);

        this.m_overlay                 = ref.instance;
        this.m_overlay.template        = this.template;
        this.m_overlay.templateContext = this.templateContext;
        this.m_overlay.config          = config;
        this.m_overlay.host            = this;
    }

    public ngOnDestroy()
    {
        this.m_outlet.dispose();
    }

    @HostListener("mousemove")
    public async handleMouseMove()
    {
        this.mouseOver          = true;
        this.m_overlay.hovering = false;

        if (this.m_disabled || this.programmatic || this.m_open) return;

        this.attemptShowTooltip(true);
    }

    @HostListener("mouseleave")
    public handleMouseLeave()
    {
        this.mouseOver = false;

        this.scheduleCloseTooltipAttempt();
    }

    @HostListener("document:click", ["$event"])
    public handleDocumentClick(event: MouseEvent)
    {
        if (!this.programmatic || !this.m_open) return;

        const target = event.target as Node;
        if (this.m_element.nativeElement.contains(target)) return;

        const overlayElement = this.m_overlay.overlayElement;
        if (overlayElement && overlayElement.contains(target)) return;

        this.closeTooltip();
    }

    public cancelScheduledCloseAttempt()
    {
        this.m_closeScheduler.cancelInvocation();
    }

    private async attemptShowTooltip(withDelay: boolean)
    {
        this.m_open = true;

        if (this.delay)
        {
            if (withDelay) await Future.delayed(this.delay);

            if (!this.m_open) return;
        }

        if (!this.m_overlay.openTooltip()) this.m_open = false;
    }

    private canClose(): boolean
    {
        return !this.mouseOver && !this.m_overlay.hovering && !this.m_overlay.opening;
    }

    public scheduleCloseTooltipAttempt()
    {
        if (this.programmatic) return;

        if (this.canClose()) this.m_closeScheduler.invoke();
    }

    public attemptCloseTooltip()
    {
        if (this.programmatic) return;

        if (this.canClose()) this.closeTooltip();
    }

    private closeTooltip()
    {
        this.m_open = false;
        this.m_cdr.markForCheck();
        this.m_overlay?.closeTooltip();
    }

    public toggleTooltip()
    {
        if (this.programmatic)
        {
            if (this.m_open)
            {
                this.closeTooltip();
            }
            else
            {
                this.attemptShowTooltip(false);
            }
        }
    }

    private getPositions(): ConnectedPosition[]
    {
        const bottom: ConnectedPosition = {
            panelClass: "mt-1",
            originX   : "center",
            originY   : "bottom",
            overlayX  : "center",
            overlayY  : "top"
        };

        const top: ConnectedPosition = {
            panelClass: "mb-1",
            originX   : "center",
            originY   : "top",
            overlayX  : "center",
            overlayY  : "bottom"
        };

        const left: ConnectedPosition = {
            panelClass: "mr-1",
            originX   : "start",
            originY   : "center",
            overlayX  : "end",
            overlayY  : "center"
        };

        const right: ConnectedPosition = {
            panelClass: "ml-1",
            originX   : "end",
            originY   : "center",
            overlayX  : "start",
            overlayY  : "center"
        };

        const bottomRight: ConnectedPosition = {
            panelClass: "ml-1",
            originX   : "end",
            originY   : "bottom",
            overlayX  : "start",
            overlayY  : "top"
        };

        const bottomLeft: ConnectedPosition = {
            panelClass: "ml-1",
            originX   : "start",
            originY   : "bottom",
            overlayX  : "start",
            overlayY  : "top"
        };

        switch (this.position || "bottom")
        {
            case "bottom":
                return [
                    bottom,
                    top
                ];

            case "top":
                return [
                    top,
                    bottom
                ];

            case "left":
                return [
                    left,
                    right
                ];

            case "right":
                return [
                    right,
                    left
                ];

            case "bottom-right":
                return [
                    bottomRight,
                    bottom,
                    top
                ];

            case "bottom-left":
                return [
                    bottomLeft,
                    bottom
                ];
        }
    }
}

export type TooltipPosition = "top" | "bottom" | "left" | "right" | "bottom-right" | "bottom-left";
